import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  Announcement,
  AnnouncementCategory,
  AnnouncementStatus,
  AnnouncementWritePayload,
  CurrentAnnouncement,
  FieldError,
  announcementService,
} from '../services/announcementService';

const CATEGORY_OPTIONS: { value: AnnouncementCategory; label: string }[] = [
  { value: 'deploy', label: 'Aggiornamento' },
  { value: 'patch_critica', label: 'Correzione critica' },
  { value: 'manutenzione', label: 'Manutenzione' },
];

const STATUS_OPTIONS: { value: AnnouncementStatus; label: string }[] = [
  { value: 'programmato', label: 'Programmato' },
  { value: 'in_corso', label: 'In Corso' },
  { value: 'completato', label: 'Completato' },
];


interface FormState {
  category: AnnouncementCategory;
  status: AnnouncementStatus;
  title: string;
  startAtLocal: string; // datetime-local input value
  downtimeEstimate: string;
  userImpact: string;
  detailsMd: string;
  softwareVersion: string;
}


const emptyForm = (): FormState => ({
  category: 'manutenzione',
  status: 'programmato',
  title: '',
  startAtLocal: '',
  downtimeEstimate: '',
  userImpact: '',
  detailsMd: '',
  softwareVersion: '',
});


function utcIsoToLocalInput(iso: string): string {
  // Convert UTC ISO string to a value the datetime-local input understands
  // (Europe/Rome timezone). We use the user's browser TZ since the design
  // pins it to Europe/Rome for Italian users.
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}


function localInputToUtcIso(local: string): string {
  // Treat the input as Europe/Rome local time, convert to UTC ISO
  if (!local) return '';
  return new Date(local).toISOString();
}


function fromAnnouncement(a: CurrentAnnouncement): FormState {
  return {
    category: a.category,
    status: a.status,
    title: a.title,
    startAtLocal: utcIsoToLocalInput(a.startAt),
    downtimeEstimate: a.downtimeEstimate ?? '',
    userImpact: a.userImpact,
    detailsMd: a.detailsMd ?? '',
    softwareVersion: a.softwareVersion ?? '',
  };
}


function formFromPayload(p: AnnouncementWritePayload): FormState {
  return {
    category: p.category,
    status: p.status,
    title: p.title,
    startAtLocal: p.start_at ? utcIsoToLocalInput(p.start_at) : '',
    downtimeEstimate: p.downtime_estimate ?? '',
    userImpact: p.user_impact,
    detailsMd: p.details_md ?? '',
    softwareVersion: p.software_version ?? '',
  };
}


function toPayload(form: FormState): AnnouncementWritePayload {
  return {
    category: form.category,
    status: form.status,
    title: form.title,
    start_at: localInputToUtcIso(form.startAtLocal),
    downtime_estimate: form.downtimeEstimate || null,
    user_impact: form.userImpact,
    details_md: form.detailsMd || null,
    software_version: form.softwareVersion || null,
  };
}


const AnnouncementsPage: React.FC = () => {
  const [current, setCurrent] = useState<CurrentAnnouncement | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pendingImport, setPendingImport] = useState<AnnouncementWritePayload | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const next = await announcementService.getCurrent();
      setCurrent(next);
      if (next) {
        setForm(fromAnnouncement(next));
      } else {
        setForm(emptyForm());
      }
      setFieldErrors({});
    } catch (e: any) {
      setToast(e?.response?.data?.message || e.message || 'Errore di caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isDraft = current && !current.isPublished;
  const isPublished = current && current.isPublished;
  const hasCurrent = current !== null;

  const onFieldChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const applyServerErrors = (errors: FieldError[]) => {
    const next: Record<string, string> = {};
    for (const e of errors) {
      next[e.field] = e.message;
    }
    setFieldErrors(next);
  };

  const onSave = async () => {
    setSaving(true);
    setFieldErrors({});
    try {
      const payload = toPayload(form);
      if (current) {
        await announcementService.update(current.id, payload);
        setToast('Avviso aggiornato');
      } else {
        await announcementService.create(payload);
        setToast('Avviso creato come bozza');
      }
      await refresh();
    } catch (err: any) {
      const body = err?.response?.data;
      if (body?.errors) {
        applyServerErrors(body.errors as FieldError[]);
        setToast('Verifica gli errori nel form');
      } else {
        setToast(body?.message || err.message || 'Errore di salvataggio');
      }
    } finally {
      setSaving(false);
    }
  };

  const onPublish = async () => {
    if (!current) return;
    setSaving(true);
    try {
      await announcementService.publish(current.id);
      setToast('Avviso pubblicato');
      await refresh();
    } catch (err: any) {
      setToast(err?.response?.data?.message || err.message || 'Errore nella pubblicazione');
    } finally {
      setSaving(false);
    }
  };

  const onArchive = async () => {
    if (!current) return;
    setSaving(true);
    setConfirmArchive(false);
    try {
      await announcementService.archive(current.id);
      setToast('Avviso archiviato');
      await refresh();
    } catch (err: any) {
      setToast(err?.response?.data?.message || err.message || 'Errore nell\'archiviazione');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!current) return;
    setSaving(true);
    setConfirmDelete(false);
    try {
      await announcementService.remove(current.id);
      setToast('Bozza eliminata');
      await refresh();
    } catch (err: any) {
      setToast(err?.response?.data?.message || err.message || 'Errore nell\'eliminazione');
    } finally {
      setSaving(false);
    }
  };

  const onExport = () => {
    if (!current) return;
    // current is a CurrentAnnouncement, which extends Announcement
    announcementService.exportToFile(current as Announcement);
  };

  const onImportClick = () => {
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;

    let payload: AnnouncementWritePayload;
    try {
      payload = await announcementService.parseImportFile(file);
    } catch (err: any) {
      setToast(err.message || 'File non valido');
      return;
    }

    if (current && current.isPublished) {
      // Scenario C — confirm archive-and-import as a new draft
      setPendingImport(payload);
    } else {
      // Scenario A (no current) or B (existing draft) — fill the form directly
      setForm(formFromPayload(payload));
      setFieldErrors({});
      setToast('Avviso importato nel form — rivedi e salva');
    }
  };

  const onConfirmImportOverPublished = async () => {
    if (!current || !pendingImport) return;
    setSaving(true);
    try {
      await announcementService.archive(current.id);
      // Switch to "create" mode locally without a full refresh (which would
      // reset the form to empty) and load the imported content.
      setCurrent(null);
      setForm(formFromPayload(pendingImport));
      setFieldErrors({});
      setToast('Avviso pubblicato archiviato — bozza importata, rivedi e crea');
    } catch (err: any) {
      setToast(err?.response?.data?.message || err.message || 'Errore nell\'archiviazione');
    } finally {
      setPendingImport(null);
      setSaving(false);
    }
  };

  const nudgeMessage = useMemo(() => {
    if (!current?.shouldNudge) return null;
    return (
      `Avviso in stato "Completato" da più di ${current.nudgeAfterMinutes} minuti. ` +
      'Archivialo per nasconderlo dalla Home.'
    );
  }, [current]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" gutterBottom>
          Avvisi Home
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileUploadIcon />}
            onClick={onImportClick}
            disabled={saving}
          >
            Importa
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={onExport}
            disabled={!current || saving}
          >
            Esporta
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Pubblica un avviso visibile a tutti gli utenti loggati nella Home.
        Categorie e tempi configurabili in Impostazioni → Avvisi Home.
        Usa Esporta/Importa per replicare un avviso sugli altri comuni.
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={onFileSelected}
      />

      {nudgeMessage && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setConfirmArchive(true)}
              disabled={saving}
            >
              Archivia ora
            </Button>
          }
        >
          {nudgeMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" error={!!fieldErrors.category}>
              <InputLabel id="category-label">Categoria</InputLabel>
              <Select
                labelId="category-label"
                label="Categoria"
                value={form.category}
                onChange={(e) =>
                  onFieldChange('category', e.target.value as AnnouncementCategory)
                }
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
              {fieldErrors.category && (
                <FormHelperText>{fieldErrors.category}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" error={!!fieldErrors.status}>
              <InputLabel id="status-label">Stato</InputLabel>
              <Select
                labelId="status-label"
                label="Stato"
                value={form.status}
                onChange={(e) =>
                  onFieldChange('status', e.target.value as AnnouncementStatus)
                }
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
              {fieldErrors.status && (
                <FormHelperText>{fieldErrors.status}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Titolo"
              value={form.title}
              onChange={(e) => onFieldChange('title', e.target.value)}
              inputProps={{ maxLength: 70 }}
              error={!!fieldErrors.title}
              helperText={fieldErrors.title || `${form.title.length}/70`}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Data e ora inizio (Europe/Rome)"
              InputLabelProps={{ shrink: true }}
              value={form.startAtLocal}
              onChange={(e) => onFieldChange('startAtLocal', e.target.value)}
              error={!!fieldErrors.start_at}
              helperText={fieldErrors.start_at}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Durata stimata dell'interruzione"
              placeholder="~20 minuti (oppure Nessuna interruzione prevista)"
              value={form.downtimeEstimate}
              onChange={(e) => onFieldChange('downtimeEstimate', e.target.value)}
              inputProps={{ maxLength: 60 }}
              error={!!fieldErrors.downtime_estimate}
              helperText={fieldErrors.downtime_estimate}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Impatto utente"
              value={form.userImpact}
              onChange={(e) => onFieldChange('userImpact', e.target.value)}
              inputProps={{ maxLength: 500 }}
              error={!!fieldErrors.user_impact}
              helperText={
                fieldErrors.user_impact ||
                `${form.userImpact.length}/500 — visibile nell'avviso`
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              label="Dettagli / Note (testo semplice)"
              value={form.detailsMd}
              onChange={(e) => onFieldChange('detailsMd', e.target.value)}
              inputProps={{ maxLength: 2000 }}
              error={!!fieldErrors.details_md}
              helperText={
                fieldErrors.details_md ||
                `${form.detailsMd.length}/2000 — mostrato nei "Dettagli" dell'avviso`
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Versione software"
              placeholder="v2.5.1"
              value={form.softwareVersion}
              onChange={(e) => onFieldChange('softwareVersion', e.target.value)}
              inputProps={{ maxLength: 40 }}
              error={!!fieldErrors.software_version}
              helperText={fieldErrors.software_version}
            />
          </Grid>
        </Grid>

        {hasCurrent && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {isPublished ? 'Pubblicato' : 'Bozza'} ·
              {' '}Modificato {new Date(current!.updatedAt).toLocaleString('it-IT', {
                timeZone: 'Europe/Rome',
              })} da {current!.updatedBy}
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : (hasCurrent ? 'Salva' : 'Crea bozza')}
          </Button>

          {isDraft && (
            <Button
              variant="contained"
              color="success"
              onClick={onPublish}
              disabled={saving}
            >
              Pubblica
            </Button>
          )}

          {isPublished && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setConfirmArchive(true)}
              disabled={saving}
            >
              Archivia
            </Button>
          )}

          {isDraft && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
            >
              Elimina bozza
            </Button>
          )}
        </Box>
      </Paper>

      <Dialog open={confirmArchive} onClose={() => setConfirmArchive(false)}>
        <DialogTitle>Archiviare l'avviso?</DialogTitle>
        <DialogContent>
          <Typography>
            Il banner sparirà dalla Home. Il record resta in database per audit ma
            non è più modificabile.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchive(false)}>Annulla</Button>
          <Button onClick={onArchive} variant="contained" color="warning">
            Archivia
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!pendingImport} onClose={() => setPendingImport(null)}>
        <DialogTitle>Sostituire l'avviso pubblicato?</DialogTitle>
        <DialogContent>
          <Typography>
            Esiste già un avviso pubblicato{current ? ` ("${current.title}")` : ''}.
            Per importare quello dal file, l'attuale verrà archiviato (sparisce
            dalla Home) e il contenuto importato sarà caricato come nuova bozza
            da rivedere e pubblicare.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingImport(null)}>Annulla</Button>
          <Button
            onClick={onConfirmImportOverPublished}
            variant="contained"
            color="warning"
          >
            Archivia e importa
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Eliminare la bozza?</DialogTitle>
        <DialogContent>
          <Typography>
            La bozza verrà rimossa dal database. Operazione non reversibile.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Annulla</Button>
          <Button onClick={onDelete} variant="contained" color="error">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        message={toast || ''}
      />
    </Box>
  );
};

export default AnnouncementsPage;
